import FocusTrap from 'focus-trap-react';
import Form from 'js/components/common/Form';
import { useTranslations } from 'js/hooks/i18n';
import { prop, pick } from 'js/utils/ramda.util';
import { Button } from 'js/components/common/Button';
import { useFormatter } from 'js/hooks/useFormatter';
import { Select } from 'js/components/common/Fields/Select';
import { ManagedPopup } from 'js/components/common/Popup';
import React, { useCallback, useState, useMemo } from 'react';
import { useOnClose } from 'js/components/common/PopupManager';
import { useAppSelector, useDeviceSelector } from 'js/store/hooks';
import { DatePicker, RANGE } from 'js/components/common/Fields/DatePicker';

import * as helpers from '../../../../helpers';
import { PERIODS, TABS, TIME_FORMATS } from '../../../../constants';

import style from './PeriodPopup.scss';

export const PeriodPopup = (props) => {
    const onClose = useOnClose();
    const { isMobile, isDesktop } = useDeviceSelector(pick(['isMobile', 'isDesktop']));
    const { data: { time, period, setValues, formData } } = props;
    const timeFormat = useAppSelector(prop('timeFormat'));
    const defaultTab = period === PERIODS.WEEKLY && TABS.DAY || period === PERIODS.ONETIME && TABS.DATE || null;
    const [tab, setTab] = useState(defaultTab);
    const onChooseTab = useCallback(setTab, [setTab]);
    const useInitialValues = () => {
        const formatter = useFormatter();

        return useMemo(() => helpers.getInitialValues({
            formData,
            formatTime: (rawTime) => formatter.getTimeShort(helpers.getParsedDateTime(rawTime))
        }), [formatter]);
    };
    const initialValues = useInitialValues();
    const translate = useTranslations('playtech.mobile.system.scheduledBreaks.DateTimePicker.');
    const onSubmitForm = useCallback(({ hours, minutes, day, date, format }) => {
        setValues({
            time,
            period,
            form: {
                hours: helpers.getHoursToSubmit(hours, minutes, format),
                minutes,
                ...day && { day },
                ...date && { date: helpers.intoDate(date) }
            }
        });
        onClose();
    }, [time, period, setValues, onClose]);

    const content = (
        <div>
            {period !== PERIODS.DAILY && <div className={style.title}>
                <button
                    className={`${style.tab} ${tab !== TABS.TIME ? style.active : ''}`}
                    onClick={() => onChooseTab(`${period === PERIODS.WEEKLY ? TABS.DAY : TABS.DATE}`)}
                >
                    {translate(`${period === PERIODS.WEEKLY ? 'daysOfWeek' : 'date'}`)}
                </button>
                <button
                    onClick={() => onChooseTab(TABS.TIME)}
                    className={`${style.tab} ${tab === TABS.TIME ? style.active : ''}`}>
                    {translate('time')}
                </button>
            </div>}
            {period === PERIODS.DAILY && <div className={style.timeTitle}>{translate('time')}</div>}
            <Form
                onSubmit={onSubmitForm}
                initialValues={initialValues}
                render={({ handleSubmit }) => (
                    <form
                        className={style.form}
                        onSubmit={event => event.preventDefault()}
                    >
                        {tab === TABS.DAY && (
                            <Select
                                name="day"
                                options={helpers.days}
                                className={style.selectDay}
                                label={translate(`day.${time}`)}
                                aria-label={translate([`day.${time}`, 'playtech.mobile.system.select.aria-label'])}
                            />
                        )}
                        {tab === TABS.DATE && (
                            <DatePicker
                                name="date"
                                range={RANGE.FUTURE}
                                className={style.picker}
                                label={translate(`day.${time}`)}
                                aria-label={translate([`day.${time}`, 'playtech.mobile.system.select.aria-label'])}
                            />
                        )}
                        {(tab === TABS.TIME || period === PERIODS.DAILY) && (
                            <div className={style.selects}>
                                <Select
                                    name="hours"
                                    className={style.select}
                                    label={translate('hours')}
                                    options={helpers.hoursOptions(timeFormat)}
                                    aria-label={translate(['hours', 'playtech.mobile.system.select.aria-label'])}
                                />
                                {!isMobile && <div className={style.sep}>{':'}</div>}
                                <Select
                                    name="minutes"
                                    className={style.select}
                                    options={helpers.minutesOptions}
                                    label={translate('minutes')}
                                    aria-label={translate(['minutes', 'playtech.mobile.system.select.aria-label'])}
                                />
                                {timeFormat === TIME_FORMATS.SHORT && (
                                    <Select
                                        name="format"
                                        options={helpers.formats}
                                        className={`${style.select} ${style.selectFormat}`}
                                        aria-label={translate('playtech.mobile.system.select.aria-label')}
                                    />
                                )}
                            </div>
                        )}
                        <div className={style.formActions}>
                            <Button
                                color='negative'
                                onClick={onClose}
                            >
                                {translate('cancel')}
                            </Button>
                            <Button
                                type="submit"
                                onClick={handleSubmit}
                            >
                                {translate('save')}
                            </Button>
                        </div>
                    </form>
                )}
            />
        </div>
    );

    return (
        <ManagedPopup
            isModal
            className={style.popup}
            classNames={{ header: style.header }}
            layerClassNames={{ layout: style.layout }}
        >
            {
                isDesktop
                    ? (
                        <FocusTrap focusTrapOptions={{
                            initialFocus: false,
                            allowOutsideClick: true
                        }} >
                            {content}
                        </FocusTrap>
                    )
                    : content
            }
        </ManagedPopup>
    );
};
